import { firstByClass, traverseParents, outerSize, parent } from 'OK/utils/dom';
import { trigger, updateBlocks } from 'OK/utils/vanilla';
import Delegate from 'OK/utils/delegate';

var ENTITY_MENU_SMALL_PARENT_WIDTH = 54;
var blockId = 'ShortcutMenu';
var activeInstance = null;

// create block element one time (after loading module) when elements with shortcut menus are really on the page
var block = document.createElement('div');
block.id = 'hook_Block_' + blockId;
document.body.appendChild(block);

OK.hookModel.captureBlockHook('body', block);

// Gif (например) добавлен
block.addEventListener('change', function () {
    if (activeInstance && activeInstance.visible()) {
        activeInstance.positingElement();
    }
});

export default class AbstractShortcutMenu {
    constructor() {
        this.holder = null;
        this.menuElement = null;
        this.showTimeout = null;
        this.hideTimeout = null; // timeout ids for tracking open/close, delays and cancel if needed
        this.ajaxRequest = null;

        this.direction = '';
        this.position = 'center';

        this._visible = false;
        this.cancelShow = false;
        this.inplace = false;
        this.detectScrollableContainer = false;
        this.menuElementHoverInListenerCalled = false;
        this.proxiedEvents = null;

        this.activateTime = 0;
        this.ignoreHoverTime = -1;
    }

    static getActiveInstance() {
        return activeInstance;
    }

    static setActiveInstance(instance) {
        activeInstance = instance;
    }

    static getBlockId() {
        return blockId;
    }

    getBlock() {
        if (this.inplace) {
            return this.block;
        }
        return block;
    }

    getHtml() {
        // override me
    }

    /**
     * Инициализация обработчиков внутри ШМ
     * @param {HTMLElement} listenerBlock
     */
    initListeners(listenerBlock) {
        this.shortcutMenuDelegate = new Delegate(listenerBlock);

        this.shortcutMenuDelegate.on('click', '.sc-menu a', this.menuElementLinkClickListener.bind(this));

        // Закрыть попап с таймаутом
        this.shortcutMenuDelegate.on('click', '.sc-menu .js-hide', this.menuElementClickListener.bind(this));

        // Закрыть попап мгновенно
        this.shortcutMenuDelegate.on('click', '.js-forceHide', function (e, target) {
            this.hideMenu({
                forceHide: !target.classList.contains('js-supress-forceHide')
            });
        }.bind(this));

        this.shortcutMenuDelegate.on('mouseenter', this.menuElementHoverInListener.bind(this));

        this.shortcutMenuDelegate.on('mouseleave', this.hoverOutListener.bind(this));

        if (OK.device.isTouch) {
            this.shortcutMenuDelegate.on('touchstart', this.holderTouchStartListener.bind(this));
        }

        if (this.proxiedEvents) {
            var self = this;
            this.proxiedEvents.split(',').forEach(function (event) {
                self.shortcutMenuDelegate.on(event, function () {
                    trigger(self.getHolder(), event);
                    self.hideMenu();
                });
            });
        }
    }

    processHTML(html) {
        var blockIds = [];

        if (this.inplace) {
            this.initBlock();
            blockIds[0] = this.blockId;
        } else {
            blockIds[0] = AbstractShortcutMenu.getBlockId();
        }

        var b = this.getBlock();
        updateBlocks(html, blockIds);
        this.initListeners(b);

        this.menuElement = firstByClass(b, 'sc-menu');
        this.positingElement();

        this.menuElement.classList.remove('sc-menu__hidden');
        this.holder.classList.add('__vis');
        AbstractShortcutMenu.setActiveInstance(this);
        this._visible = true;
    }

    /**
     * Show shortcut menu
     */
    showMenu() {
        clearTimeout(this.hideTimeout);
        this.showTimeout = null;

        //Точно такое же меню от этого элемента уже показано. Не надо ничего делать
        if (this.visible()) {
            return;
        }

        if (activeInstance) {
            // Не должно быть открыто больше 1 ШМ
            activeInstance.hide();
        }

        this.processHTML(this.getHtml());

        trigger(this.holder, 'shortcut-menu-show');

        if (this.hideOnScroll) {
            this.oneScrollListener = {
                element: this.getClosestScrollable(true),
                type: 'scroll',
                handler: this.hoverOutListener.bind(this)
            };

            this.oneScrollListener.element.addEventListener('scroll', this.oneScrollListener.handler);
        }
    }

    hideMenu(reason) {
        // override me!
        // блокируем таймаут показа меню, т.к. уже показывать меню не надо
        this.clearShowTimeout();
        this.clearHideTimeout();

        trigger(this.holder, 'shortcut-menu-hide', reason);

        if (this.menuElement) {
            this.menuElement.classList.add('sc-menu__hidden');
            OK.hookModel.setHookContent(blockId, '');
            this.menuElement = null;
        }
        this.holder.classList.remove('__vis');

        if (this.shortcutMenuDelegate) {
            // Отписка от событий внутри ШМ
            this.shortcutMenuDelegate.off();
        }

        this._visible = false;

        this.removeOneTimeListener(this.oneScrollListener);
        AbstractShortcutMenu.setActiveInstance(null);
    }

    hide() {
        this.clearShowTimeout();
        this.clearHideTimeout();
        if (this.menuElement) {
            this.hideMenu();
        } else if (this.ajaxRequest) {
            // Если выполняется запрос на получение содержимого меню,
            // отменяем отображение меню (когда результат кэшируется) или отменяем запрос
            if (this.cacheable) {
                this.cancelShow = true;
            } else {
                this.ajaxClose();
            }
        }
    }

    /**
     * Ближайший скроллирующийся родитель или window
     * @param {?boolean} [detectScrollBar] проверять ли реальное наличие скроллбара
     * @return {HTMLElement|Window}
     */
    getClosestScrollable(detectScrollBar) {
        if (!this.closestScrollable) {
            var overflowRegex = /(auto|scroll)/;
            var closestScrollable = traverseParents(this.holder, function (currentElement) {
                var style = window.getComputedStyle(currentElement);
                var hasOverflowStyle = overflowRegex.test(style.overflow + style.overflowY + style.overflowX);
                return hasOverflowStyle && (!detectScrollBar || (currentElement.scrollHeight > currentElement.clientHeight));
            });

            this.closestScrollable = closestScrollable || window;
        }

        return this.closestScrollable;
    }

    isTopDirection(parentHeight, menuElementHeight) {
        var holderTop = this.holder.getBoundingClientRect().top;
        if (this.detectScrollableContainer) {
            var holderHeight = this.holder.getBoundingClientRect().height;
            // скроллируемый контейнер
            var scrollable = this.getClosestScrollable();
            // позиция holder'а относительно верхнего края scrollable
            var holderTopInScrollable = holderTop - scrollable.getBoundingClientRect().top;
            // центр скроллируемого контейнреа
            var centerOfScrollable = scrollable.clientHeight - holderHeight;
            var menuBottomPos = scrollable.scrollTop + holderTopInScrollable + holderHeight + menuElementHeight;
            var scrollableBottom = scrollable.scrollHeight;

            // занимаем большую часть относительно центра контейнера
            // но не опускаемся ниже его контента (чтобы не отодвигать футер)
            return centerOfScrollable / 2 < holderTopInScrollable
                || (menuBottomPos > scrollableBottom);
        } else {
            return window.innerHeight - parentHeight - holderTop < menuElementHeight;
        }
    }

    isInsideViewportWhileCentered(menuElementWidth) {
        var holder = this.holder.getBoundingClientRect();
        var holderLeft = holder.left;
        var containerRight = window.innerWidth - OK.scrollBar.width;
        if (this.detectScrollableContainer) {
            var containerRect = this.getClosestScrollable().getBoundingClientRect();
            holderLeft = holderLeft - containerRect.left;
            containerRight = containerRect.right;
        }

        var menuElementLeft = holderLeft + (holder.width - menuElementWidth) / 2;
        var menuElementRight = menuElementLeft + menuElementWidth;

        return menuElementLeft > 0 && menuElementRight < containerRight;
    }

    toRightFromHolder(parentOffset, menuElementWidth) {
        if (this.detectScrollableContainer) {
            var containerRect = this.getClosestScrollable().getBoundingClientRect();
            var holderLeft = this.holder.getBoundingClientRect().left - containerRect.left;
            return containerRect.width / 2 > holderLeft;
        } else {
            return (window.innerWidth - OK.scrollBar.width) > (parentOffset.left + menuElementWidth);
        }
    }

    visible() {
        return this._visible;
    }

    getOffset(element) {
        var rect = element.getBoundingClientRect();
        return {
            left: rect.left + window.pageXOffset,
            top: rect.top + window.pageYOffset
        };
    }

    /**
     * Sets shortcut menu position up
     */
    positingElement() {
        var parentHeight = outerSize(this.holder, false, false);
        //Наш новый блок должен быть position: relative
        var parentOffset = this.inplace
            ? {top: 0, left: 0}
            : this.getOffset(this.holder);

        var parentWidth = outerSize(this.holder, true, false);
        var menuElementWidth = outerSize(this.menuElement, true, true);

        var position = this.position;
        if (position === 'center' && !this.isInsideViewportWhileCentered(menuElementWidth)) {
            position = 'auto';
        }

        // Удалить лишний отступ для обсчета геометрии
        if (position !== 'center' && position !== 'auto') {
            this.menuElement.classList.add('__noarrow');
        }

        var topDirection;
        var menuElementHeight = outerSize(this.menuElement, false, true);

        if (this.direction === 'top_force' || this.direction === 'bottom_force') {
            topDirection = this.direction === 'top_force';
        } else if (this.direction === 'top') { // приоритет на отрисовку вверх
            topDirection = this.getOffset(this.holder).top - window.pageYOffset - 90 > menuElementHeight;
        } else if (this.direction === 'bottom') { // приоритет на отрисовку вниз
            topDirection = false;
        } else {
            topDirection = this.isTopDirection(parentHeight, menuElementHeight);
        }

        // display below (default) or above target, if below is not enough of space
        // but don't overlap toolbar
        var top = parentOffset.top;
        var styleRules = [];

        if (topDirection) {
            this.menuElement.classList.add("sc-menu__top");
            var bottom;
            if (this.inplace) {
                bottom = top + parentHeight;
                //Позиционируем по bottom чтобы в случае, когда размеры меню неявно меняются оно уезжало в нужном направлении
                styleRules.push('top: auto; bottom: ' + Math.round(bottom) + 'px;');
            } else {
                top -= menuElementHeight;
                styleRules.push('bottom: auto; top: ' + Math.round(top) + 'px;');
            }
        } else {
            this.menuElement.classList.remove("sc-menu__top");
            if (!this.inplace) {
                top += parentHeight;
            }
            styleRules.push('bottom: auto; top: ' + Math.round(top) + 'px;');
        }

        var leftCoords = Math.round(parentOffset.left - (menuElementWidth - parentWidth));
        var rightCoords = Math.round(parentOffset.left);
        var centerCoords = Math.round(parentOffset.left + ((parentWidth - menuElementWidth) / 2));

        switch (position) {
            case 'left':
                styleRules.push('left: ' + leftCoords + 'px;');
                break;

            case 'right':
                styleRules.push('left: ' + rightCoords + 'px;');
                break;

            case 'auto':
                var arw = firstByClass(this.menuElement, 'entity-shortcut-menu_arw'), correction = 0,
                    smallParentWidth = arw ? ENTITY_MENU_SMALL_PARENT_WIDTH : 0,
                    rightFromHolder = this.toRightFromHolder(parentOffset, menuElementWidth);

                if (rightFromHolder) {
                    this.menuElement.classList.remove('__right');
                    this.menuElement.classList.add('__left');
                } else {
                    this.menuElement.classList.add('__right');
                    this.menuElement.classList.remove('__left');
                }

                // для меню группы и юзера поправка заранее известна (там вёрстка на основе svg)
                // если это другое меню - пытаемся рассчитать
                if (!smallParentWidth) {

                    arw = firstByClass(this.menuElement, 'sc-menu_arw');

                    if (arw) {
                        // треугольник найден
                        var arwRect = arw.getBoundingClientRect();

                        if (arwRect.width) {
                            // треугольник видимый, расчитываем поправку
                            var menuRect = this.menuElement.getBoundingClientRect();

                            if (rightFromHolder) {
                                smallParentWidth = (arwRect.left - menuRect.left) * 2 + arwRect.width;
                            } else {
                                smallParentWidth = ((menuRect.left + menuRect.width) - (arwRect.left + arwRect.width)) * 2 + arwRect.width;
                            }
                        }
                    }
                }

                if (parentWidth < smallParentWidth) {
                    correction = (smallParentWidth - parentWidth) / 2;
                }

                if (rightFromHolder) {
                    styleRules.push('left: ' + (rightCoords - correction) + 'px;');
                } else {
                    styleRules.push('left: ' + (leftCoords + correction) + 'px;');
                }
                break;

            default:
                styleRules.push('left: ' + centerCoords + 'px;');
                break;
        }

        this.menuElement.setAttribute('style', styleRules.join(""));
    }

    /**
     * При наличии активного сетевого запроса закрываем его
     *
     * @param cb
     */
    ajaxClose(cb) {
        if (this.ajaxRequest) {
            this.ajaxRequest.xhr.abort();
            this.ajaxRequest = null;
            this.cancelShow = false;

            if (typeof cb === 'function') {
                cb();
            }
        }
    }

    /**
     * Listener for hover in event. Show shortcut menu after show delay
     */
    hoverInListener(e) {
        if ((OK.device.isTouch || (Date.now() - this.activateTime) <= this.ignoreHoverTime) && e) {
            return;
        }

        if (activeInstance !== this) {
            if (activeInstance) {
                // Не должно быть открыто больше 1 ШМ
                //если в этом месте скрывать ШМ, то в списках где людей где шм висит отдельно на фатке и на имени невозможно навести на фоточное ШМ
                // activeInstance.hide();
            }

            // перед установкой активного инстанса меню нужно скрывать предыдущий activeInstance.hide()
            if (!activeInstance) {
                AbstractShortcutMenu.setActiveInstance(this);
            }
        }

        this.clearHideTimeout();
        // не даем ШМ начать показываться, если оно и так видимо
        if (!this.showTimeout && !this.visible()) {
            this.showTimeout = setTimeout(this.showMenu.bind(this), this.showDelay);
        }
    }

    /**
     * Listener for hover in event/ Hide shortcut menu after hide delay
     */
    hoverOutListener() {
        this.clearShowTimeout();
        this.ajaxClose();
        if (!this.hideTimeout && this.visible()) {
            this.hideTimeout = setTimeout(function () {
                if (this.hideOnBlurElement !== document.activeElement) {
                    this.hideMenu();
                } else {
                    this.hideTimeout = null;
                }
            }.bind(this), this.hideDelay);
        }
    }

    /**
     * Listener for click event shows shortcut menu immediately without delay
     */
    menuElementHoverInListener() {
        this.menuElementHoverInListenerCalled = true;
        if (this.hideTimeout) {
            this.hideTimeout = clearTimeout(this.hideTimeout);
        }
    }

    /**
     * Listener for click event on links in shortcut menu
     * @param {Event} e event
     */
    menuElementLinkClickListener(e) {
        if (!parent(e.target, 'js-doNotHide', this.block)) {
            this.menuElementClickListener();
        }
    }

    menuElementClickListener() {
        if (true === this.hideTimeout) {
            this.hideTimeout = null;
        }
        this.hoverOutListener();
    }

    /**
     * stops propagation to prevent body touch start listener and make link works on second touch
     * @param {Event} e event
     */
    holderTouchStartListener(e) {
        e.stopPropagation();
    }

    clearShowTimeout() {
        if (this.showTimeout) {
            this.showTimeout = clearTimeout(this.showTimeout); // if menu is not opened yet, prevent opening
        }
    }

    clearHideTimeout() {
        if (this.hideTimeout) {
            this.hideTimeout = clearTimeout(this.hideTimeout); // if menu is not closed yet, prevent closing
        }
    }

    removeOneTimeListener(listenerObject) {
        if (listenerObject) {
            listenerObject.element.removeEventListener(listenerObject.type, listenerObject.handler);
            listenerObject = null;
        }
    }
}

export const getActiveInstance = AbstractShortcutMenu.getActiveInstance;
export const setActiveInstance = AbstractShortcutMenu.setActiveInstance;
export const getBlockId = AbstractShortcutMenu.getBlockId;
